import LegalLayout, { type LegalSection } from "./components/LegalLayout";

const sections: LegalSection[] = [
  {
    id: "information-we-collect",
    heading: "Information We Collect",
    paragraphs: [
      "Voxmaster LLC (\"Voxmaster,\" \"we,\" \"us,\") collects information necessary to operate a wholesale voice termination, DID origination, and SIP trunking platform. This includes information you provide directly, information generated through use of our services, and information we receive from third parties in connection with provisioning and fraud prevention.",
    ],
    items: [
      "Account and business information — company name, business address, tax and registration identifiers, and authorized contact details.",
      "Contact information — names, work email addresses, phone numbers, and messenger handles of your authorized representatives.",
      "Billing and payment information — invoicing details and, where applicable, payment instrument references processed by our payment providers.",
      "Traffic and usage data — call detail records (CDRs), routing statistics, quality metrics, and signaling logs associated with your trunks.",
      "Technical data — IP addresses, device identifiers, and connection metadata used to operate, secure, and troubleshoot the service.",
    ],
  },
  {
    id: "how-we-use",
    heading: "How We Use Your Information",
    paragraphs: [
      "We use the information we collect to deliver, maintain, and improve our services, and to meet our legal and regulatory obligations as a licensed telecommunications carrier.",
    ],
    items: [
      "Provisioning and operating voice termination, DID origination, and SIP trunking services.",
      "Authenticating users, securing the platform, and detecting or preventing fraud and abuse.",
      "Billing, invoicing, and resolving disputes or support requests.",
      "Monitoring quality of service and producing routing and performance reports.",
      "Complying with legal obligations, lawful requests, and industry regulations applicable to telecommunications providers.",
    ],
  },
  {
    id: "legal-basis",
    heading: "Legal Basis for Processing",
    paragraphs: [
      "Our processing is based on the necessity of performing our contract with you, our legitimate interests in operating and securing a carrier-grade network, and compliance with legal obligations. Where we rely on consent, you may withdraw it at any time without affecting the lawfulness of processing carried out before withdrawal.",
    ],
  },
  {
    id: "data-retention",
    heading: "Data Retention",
    paragraphs: [
      "Call detail records and billing data are retained for the period required by applicable telecommunications regulations and our internal retention schedules. Account information is retained while your account remains active and for a reasonable period afterward to satisfy legal, accounting, and audit requirements. We periodically review and delete data that is no longer needed.",
    ],
  },
  {
    id: "sharing",
    heading: "How We Share Information",
    paragraphs: [
      "We do not sell your personal information. We share information only as necessary to provide the service and comply with the law, including with:",
    ],
    items: [
      "Subprocessors and service providers that help us operate the platform, host infrastructure, process payments, or provide support, subject to confidentiality obligations.",
      "Regulatory authorities, law enforcement, or courts when required by law or a valid legal process.",
      "Business partners and interconnection providers to the extent necessary to route and terminate traffic.",
      "A successor entity in the event of a merger, acquisition, or asset sale.",
    ],
  },
  {
    id: "data-subject-rights",
    heading: "Your Rights",
    paragraphs: [
      "Depending on your jurisdiction, you may have the right to access, correct, delete, restrict, or port your personal information, and to object to certain processing. To exercise these rights, contact us at legal@voxmaster.com. We will respond within the timeframes required by applicable law.",
    ],
  },
  {
    id: "security",
    heading: "Data Security",
    paragraphs: [
      "We employ technical and organizational measures — including encryption in transit, access controls, network segmentation, and continuous monitoring — designed to protect your information against unauthorized access, alteration, disclosure, or destruction. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    id: "international-transfers",
    heading: "International Data Transfers",
    paragraphs: [
      "As a global carrier, your information may be transferred to and processed in countries other than your own. Where required, we rely on appropriate safeguards, including standard contractual clauses, to protect data transferred across borders.",
    ],
  },
  {
    id: "cookies",
    heading: "Cookies and Analytics",
    paragraphs: [
      "Our website uses essential cookies and, with your consent where required, analytics to understand how visitors use our site. You can control cookie preferences through your browser settings.",
    ],
  },
  {
    id: "changes-contact",
    heading: "Changes to This Policy and Contact",
    paragraphs: [
      "We may update this Privacy Policy from time to time. Material changes will be communicated through our website or via email. For privacy-related questions or to exercise your rights, contact our compliance team at legal@voxmaster.com or by mail at Voxmaster LLC, 3960 Howard Hughes Parkway, Suite 500, Las Vegas, Nevada 89169, United States.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="August 15, 2026"
      intro="This Privacy Policy explains how Voxmaster LLC collects, uses, shares, and protects your information when you use our wholesale voice termination, DID origination, and SIP trunking services."
      sections={sections}
      active="privacy"
    />
  );
}